import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    TextField,
    Paper,
    Button,
    IconButton,
    Tooltip,
    Stack,
    useTheme,
    Breadcrumbs,
    LinearProgress,
    Select,
    MenuItem,
    InputAdornment,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { formGap } from "../../../ui-components/global/utils";
import {
    resetTrackingData,
    HomeworkEvalAnalyticsGet,
} from "../../../store/reducers/Formapireducer";
import useMediaQuery from "@mui/material/useMediaQuery";
import { CheckinAutocomplete } from "../../../ui-components/global/Autocomplete";
import { Formik } from "formik";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import Swal from "sweetalert2";
import { useProSidebar } from "react-pro-sidebar";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import ResetTvIcon from "@mui/icons-material/ResetTv";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import { tokens } from "../../../Theme";
import {
    Chart,
    ArcElement,
    Tooltip as CJTooltip,
    Legend,
    CategoryScale,
    LinearScale,
    BarElement,
} from "chart.js";
import { Doughnut, Bar } from "react-chartjs-2";
import { getConfig } from "../../../config";

Chart.register(ArcElement, CJTooltip, Legend, CategoryScale, LinearScale, BarElement);

// ─── CONSTANTS ────────────────────────────────────────────────────────────────

const STATUS_MAP = {
    EV: { label: "Evaluated", bg: "#e1f5ee", color: "#085041" },
    PE: { label: "Pending", bg: "#faeeda", color: "#633806" },
    NE: { label: "Not Evaluated", bg: "#eeedfe", color: "#3c3489" },
    YS: { label: "Yet to submit", bg: "#fcebeb", color: "#791f1f" },
};

// const KPI_CONFIG = [
//     {
//         key: "total",
//         label: "Total students",
//         sub: "All sections",
//         accent: "#534ab7",
//         bg: "#eeedfe",
//         labelColor: "#3c3489",
//         valColor: "#26215c",
//         subColor: "#534ab7",
//     },
//     {
//         key: "ev",
//         label: "Evaluated",
//         sub: null,
//         accent: "#1d9e75",
//         bg: "#e1f5ee",
//         labelColor: "#085041",
//         valColor: "#04342c",
//         subColor: "#0f6e56",
//     },
//     {
//         key: "pe",
//         label: "Pending",
//         sub: "Awaiting review",
//         accent: "#ef9f27",
//         bg: "#faeeda",
//         labelColor: "#633806",
//         valColor: "#412402",
//         subColor: "#854f0b",
//     },
//     {
//         key: "ys",
//         label: "Yet to submit",
//         sub: "Not submitted",
//         accent: "#e24b4a",
//         bg: "#fcebeb",
//         labelColor: "#791f1f",
//         valColor: "#501313",
//         subColor: "#a32d2d",
//     },
// ];
const KPI_CONFIG = [
    {
        key: "total",
        label: "Total students",
        accent: "#c554d1",
        bg: "#f5e1f5",
        labelColor: "#e530eb",
        valColor: "#04342c",
        subColor: "#e530eb",
    },
    {
        key: "ev",
        label: "Evaluated",
        accent: "#1d9e75",
        bg: "#e1f5ee",
        labelColor: "#085041",
        valColor: "#04342c",
        subColor: "#0f6e56",
    },
    {
        key: "NE",
        label: "Not Evaluated",
        accent: "#534ab7",
        bg: "#eeedfe",
        labelColor: "#3c3489",
        valColor: "#26215c",
        subColor: "#534ab7",

    },
    {
        key: "pe",
        label: "Pending",
        accent: "#ef9f27",
        bg: "#faeeda",
        labelColor: "#633806",
        valColor: "#412402",
        subColor: "#854f0b",
    },
    // {
    //     key: "re",
    //     label: "Resubmit",
    //     sub: "Resubmitted",
    //     accent: "#7f77dd",
    //     bg: "#eeedfe",
    //     labelColor: "#3c3489",
    //     valColor: "#412402",
    //     subColor: "#854f0b",
    // },
    {
        key: "ys",
        label: "Yet to submit",
        accent: "#e24b4a",
        bg: "#fcebeb",
        labelColor: "#791f1f",
        valColor: "#501313",
        subColor: "#a32d2d",
    },
];
const PIE_NAMES = ["Evaluated", "Pending", "Yet to Submit"];
const PIE_COLORS = ["#1d9e75", "#ef9f27", "#7f77dd", "#7f77dd", "#e24b4a"];

const BAR_COLORS = {
    "0-40": "#f09595",
    "40-60": "#fac775",
    "60-80": "#97c459",
    "80-90": "#85b7eb",
    "90-100": "#afa9ec",
};

// ─── SMALL COMPONENTS ─────────────────────────────────────────────────────────

const KpiCard = ({ cfg, value, subOverride }) => (
    <Box
        sx={{
            background: cfg.bg,
            borderLeft: `3px solid ${cfg.accent}`,
            borderRadius: "8px",
            p: "12px 14px",
            flex: 1,
            minWidth: 0,
        }}
    >
        <Typography
            sx={{
                fontSize: "10px",
                color: cfg.labelColor,
                // textTransform: "uppercase",
                letterSpacing: ".4px",
                mb: "5px",
            }}
        >
            {cfg.label}
        </Typography>
        <Typography
            sx={{ fontSize: "28px", fontWeight: 500, color: cfg.valColor, lineHeight: 1 }}
        >
            {value}
        </Typography>
        <Typography sx={{ fontSize: "11px", color: cfg.subColor, mt: "4px" }}>
            {subOverride || cfg.sub}
        </Typography>
    </Box>
);

const StatusBadge = ({ code }) => {
    const s = STATUS_MAP[code] || { label: code, bg: "#f1f0e8", color: "#5f5e5a" };
    return (
        <Box
            component="span"
            sx={{
                display: "inline-block",
                px: "9px",
                py: "2px",
                borderRadius: "999px",
                fontSize: "11px",
                fontWeight: 500,
                background: s.bg,
                color: s.color,
            }}
        >
            {s.label}
        </Box>
    );
};

const PieLegendRow = ({ name, value, total, color }) => {
    const pct = total ? Math.round((value / total) * 100) : 0;
    return (
        <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap="7px">
                <Box
                    sx={{
                        width: 9,
                        height: 9,
                        borderRadius: "50%",
                        background: color,
                        flexShrink: 0,
                    }}
                />
                <Typography sx={{ fontSize: "12px", color: "#64748b" }}>{name}</Typography>
            </Box>
            <Box display="flex" alignItems="baseline" gap="3px">
                {/* <Typography sx={{ fontSize: "12px", fontWeight: 500, color: "#0f172a" }}>
                    {value}
                </Typography> */}
                <Typography sx={{ fontSize: "10px", color: "#94a3b8" }}>{pct}%</Typography>
            </Box>
        </Box>
    );
};

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

const HomeAnalytics = () => {
    const isNonMobile = useMediaQuery("(min-width:600px)");
    const navigate = useNavigate();
    const params = useParams();
    const config = getConfig();
    const location = useLocation();
    const mode = params.Mode;
    const dispatch = useDispatch();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const SubscriptionCode = sessionStorage.getItem("SubscriptionCode");
    const is003Subscription = SubscriptionCode?.endsWith("003");
    const sliceSubscriptionCode = SubscriptionCode?.slice(-3);
    const compID = sessionStorage.getItem("companyId");
    const EMPID = sessionStorage.getItem("EmpId");

    const listViewurl = useSelector((state) => state.globalurl.listViewurl);
    const { toggleSidebar, broken, rtl } = useProSidebar();

    const [hasFetched, setHasFetched] = useState(false);
    const [errorMsgData] = useState(null);
    const [selectedStandard, setSelectedStandard] = useState(null);
    const [selectedSubject, setSelectedSubject] = useState(null);
    const [statusFilter, setStatusFilter] = useState("All");
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(8);
    const [rows, setRows] = useState([]);
    const [showCharts, setShowCharts] = useState(false);

    // Redux
    const HWteachdata = useSelector((state) => state.formApi.HWteachdata);
    const HWAnalyticsdata = useSelector((state) => state.formApi.HWAnalyticsdata);
    const HWAnalyticsloading = useSelector((state) => state.formApi.HWAnalyticsloading);

    const pieData = HWAnalyticsdata?.AnalayticsData?.pieData || [];
    const barData = HWAnalyticsdata?.AnalayticsData?.barData || [];
    const analyticsData = HWAnalyticsdata?.AnalayticsData?.analyticsData || [];
    const analyticsObj = HWAnalyticsdata?.AnalayticsData;
    const summarydata = HWAnalyticsdata?.AnalayticsData?.summaryData || {};
    console.log("summarydata:", summarydata);
    const hasData =
        analyticsObj &&
        analyticsObj.analyticsData?.length > 0 &&
        analyticsObj.pieData?.length > 0 &&
        analyticsObj.barData?.length > 0;

    useEffect(() => {
        dispatch({ type: "formApi/clearHWAnalyticsdata" });
    }, []);

    useEffect(() => {
        if (mode === "E" && HWteachdata?.data?.length > 0) setRows(HWteachdata.data);
    }, [mode, HWteachdata]);

    useEffect(() => {
        if (hasFetched && hasData) {
            setTimeout(() => {
                window.dispatchEvent(new Event("resize"));
            }, 300);
        }
    }, [hasFetched, hasData]);
    const today = new Date().toISOString().split("T")[0];
    const AttInitialvalues = {
        description: HWteachdata?.Description || "",
        targetdate: "",
        date: today,
        ProName: null,
        department: null,
        Attachment: HWteachdata?.Attachment,
    };

    const fnLogOut = (props) => {
        Swal.fire({
            title: errorMsgData?.Warningmsg?.[props] || `${props}?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: props,
        }).then((result) => {
            if (result.isConfirmed) {
                if (props === "Logout") navigate("/");
                if (props === "Close") navigate("/Apps/Chart");
            }
        });
    };

    const handleApply = (values) => {
        setHasFetched(true);
        setPage(0);
        dispatch(
            HomeworkEvalAnalyticsGet({
                CompanyID: compID,
                StandardID: selectedStandard?.RecordID,
                SubjectID: selectedSubject?.RecordID,
                GivenDate: values.date,
                TargetDate: values.targetdate,
            })
        );
    };

    const handleReset = (resetForm) => {
        resetForm();
        setSelectedStandard(null);
        setSelectedSubject(null);
        setHasFetched(false);
        setStatusFilter("All");
        setSearch("");
        setPage(0);
        dispatch({ type: "formApi/clearHWAnalyticsdata" });
    };

    // ── KPI derived values ─────────────────────────────────────────────────
    // const total = pieData.reduce((s, d) => s + (d.value || 0), 0);
    // const evCount = pieData.find((d) => d.name === "Evaluated")?.value || 0;
    // const peCount = pieData.find((d) => d.name === "Pending")?.value || 0;
    // const ysCount = pieData.find((d) => d.name === "Yet to Submit")?.value || 0;
    // const evPct = total ? Math.round((evCount / total) * 100) : 0;

    const total = summarydata?.TotalStudents || 0;
    const totalHmwrk = summarydata?.TotalHomeworks || 0;
    const evCount = summarydata?.Evaluated || 0;
    const evPct = summarydata?.EvaluatedPct || 0;
    const peCount = summarydata?.Pending || 0;
    const PePct = summarydata?.PendingPct || 0;
    const ysCount = summarydata?.YetToSubmit || 0;
    const YsPct = summarydata?.YetToSubmitPct || 0;
    const NECount = summarydata?.NotEvaluated || 0;
    const NEPct = summarydata?.NotEvaluatedPct || 0;
    console.log("summarydata:", total, evCount, peCount, ysCount, totalHmwrk, evPct, PePct, YsPct, NECount, NEPct);
    const kpiValues = { total, ev: evCount, pe: peCount, ys: ysCount, NE: NECount };

    // ── Donut chart ─────────────────────────────────────────────────────────
    const activePie = PIE_NAMES.map((name, i) => ({
        name,
        value: pieData.find((d) => d.name === name)?.value || 0,
        color: PIE_COLORS[i],
    })).filter((d) => d.value > 0);

    const doughnutData = {
        labels: activePie.map((d) => d.name),
        datasets: [
            {
                data: activePie.map((d) => d.value),
                backgroundColor: activePie.map((d) => d.color),
                borderWidth: 2,
                borderColor: "#ffffff",
                hoverOffset: 5,
            },
        ],
    };

    const doughnutOptions = {
        // responsive:  false,
        responsive: true,
        maintainAspectRatio: false,
        // cutout:      "65%",
        // plugins: {
        //     legend: { display: false },
        //     tooltip: {
        //         callbacks: {
        //             label: (ctx) =>
        //                 ` ${ctx.label}: ${ctx.raw} (${
        //                     total ? Math.round((ctx.raw / total) * 100) : 0
        //                 }%)`,
        //         },
        //     },
        // },
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    label: (ctx) =>
                        ` ${ctx.label}: ${ctx.raw} (${total ? Math.round((ctx.raw / total) * 100) : 0
                        }%)`,
                },
            },
        },
    };

    // ── Bar chart ────────────────────────────────────────────────────────────
    const safeBarData = barData?.length > 0 ? barData : [];

    const barChartData = {
        labels: safeBarData.map((d) => d.range),
        datasets: [
            {
                data: safeBarData.map((d) => d.value),
                backgroundColor: safeBarData.map(
                    (d) => BAR_COLORS[d.range?.trim()] || "#afa9ec"
                ),
                borderRadius: 5,
                borderSkipped: false,
                barPercentage: 0.6,
            },
        ],
    };

    const barOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    label: (ctx) =>
                        ` ${ctx.raw} student${ctx.raw !== 1 ? "s" : ""}`,
                },
            },
        },
        scales: {
            x: {
                grid: { display: false },
                ticks: { font: { size: 11 }, color: "#888780", autoSkip: false },
            },
            y: {
                beginAtZero: true,
                ticks: { stepSize: 1, precision: 0, font: { size: 11 }, color: "#888780" },
                grid: { color: "rgba(136,135,128,.12)" },
            },
        },
    };

    // ── Table filtering ──────────────────────────────────────────────────────
    // const filteredData = analyticsData.filter((row) => {
    //     const matchStatus =
    //         statusFilter === "All" || row.Status === statusFilter;
    //     const matchSearch = row.Student?.toLowerCase().includes(
    //         search.toLowerCase()
    //     );
    //     return matchStatus && matchSearch;
    // });
    const filteredData = analyticsData.filter((row) => {
        //     const matchStatus =
        // statusFilter === "All" ||
        // row.StatusCode?.trim() === statusFilter;
         // normalize backend status
    const status =
        row.StatusCode?.trim()?.toUpperCase() ||
        row.Status?.trim()?.toUpperCase() ||
        "";

    // dropdown filter
    const matchStatus =
        statusFilter === "All" ||
        status === statusFilter;
    // const matchStatus =
    //     statusFilter === "All" || row.Status === statusFilter;

    const searchText = search.toLowerCase();

    // const matchSearch = Object.values(row).some((value) =>
    //     String(value || "")
    //         .toLowerCase()
    //         .includes(searchText)
    // );

       // ✅ Search only specific columns, not Remarks (which displays "—")
    const matchSearch =
        !searchText ||
        String(row.Rank ?? "").toLowerCase().includes(searchText) ||
        String(row.Subject ?? "").toLowerCase().includes(searchText) ||
        String(row.Description ?? "").toLowerCase().includes(searchText) ||
        String(row.Student ?? "").toLowerCase().includes(searchText) ||
        Number(row.Score).toFixed(0).toString().includes(searchText) ||
        // String(row.Score ?? "").toLowerCase().includes(searchText) ||
      String(row.Status ?? "").toLowerCase().includes(searchText) ||
        String(row.Remarks ?? "").toLowerCase().includes(searchText);
        // Note: Remarks uses raw value here, not the "—" fallback

    return matchStatus && matchSearch;
});

    // shared card sx
    const cardSx = {
        background: "#fff",
        border: "0.5px solid #e2e8f0",
        borderRadius: "12px",
        p: "16px",
        overflow: "hidden",
    };

    const thSx = {
        fontSize: "10px",
        fontWeight: 500,
        color: "#64748b",
        // textTransform:   "uppercase",
        letterSpacing: ".4px",
        py: "9px",
        px: "10px",
        borderBottom: "0.5px solid #e2e8f0",
        whiteSpace: "nowrap",
        background: "#f8fafc",
    };

    const tdSx = {
        fontSize: "12px",
        color: "#334155",
        py: "9px",
        px: "10px",
        borderBottom: "0.5px solid #e2e8f0",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    };

    return (
        <React.Fragment>
            {/* ── HEADER ───────────────────────────────────────────────────── */}
            <Paper
                elevation={3}
                sx={{ margin: "0px 10px", background: "#F2F0F0", height: "50px" }}
            >
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    px={2}
                    py={1.5}
                >
                    <Box display="flex" alignItems="center" gap={1}>
                        {broken && !rtl && (
                            <IconButton onClick={() => toggleSidebar()} size="small">
                                <MenuOutlinedIcon />
                            </IconButton>
                        )}
                        <Box display={isNonMobile ? "flex" : "none"} alignItems="center">
                            <Breadcrumbs
                                separator={
                                    <NavigateNextIcon sx={{ color: "#94a3b8", fontSize: 16 }} />
                                }
                            >
                                <Typography
                                    onClick={() => navigate(-1)}
                                    sx={{
                                        fontSize: "18px",
                                        fontWeight: 500,
                                        color: "#0f172a",
                                        cursor: "pointer",
                                        "&:hover": { color: "#534ab7" },
                                    }}
                                >
                                    Homework Analytics
                                </Typography>
                            </Breadcrumbs>
                        </Box>
                    </Box>
                    <Box display="flex" gap={0.5}>
                        <Tooltip title="Close">
                            <IconButton
                                onClick={() => fnLogOut("Close")}
                                size="small"
                                sx={{ color: "#ef4444" }}
                            >
                                <ResetTvIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Logout">
                            <IconButton
                                onClick={() => fnLogOut("Logout")}
                                size="small"
                                sx={{ color: "#ef4444" }}
                            >
                                <LogoutOutlinedIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
            </Paper>

            {/* ── FILTER FORM ──────────────────────────────────────────────── */}
            <Paper
                elevation={0}
                sx={{ m: "10px", border: "1px solid #e2e8f0", borderRadius: "12px" }}
            >
                <Formik
                    initialValues={AttInitialvalues}
                    enableReinitialize
                    onSubmit={(values, { resetForm }) => {
                        setTimeout(() => { }, 100);
                    }}
                >
                    {({
                        errors,
                        touched,
                        handleBlur,
                        handleChange,
                        values,
                        handleSubmit,
                        resetForm,
                        setFieldValue,
                    }) => (
                        <form
                            onSubmit={handleSubmit}
                            onReset={() => {
                                resetForm();
                                setRows([]);
                                dispatch(resetTrackingData());
                            }}
                        >
                            <Box
                                display="grid"
                                // gridTemplateColumns="repeat(2, minmax(0, 1fr))"
                                gridTemplateColumns={{
                                    xs: "1fr",
                                    sm: "1fr 1fr",
                                }}
                                gap={formGap}
                                p={2}
                                sx={{
                                    "& > div": {
                                        gridColumn: isNonMobile ? undefined : "span 4",
                                    },
                                }}
                            >
                                <TextField
                                    name="date"
                                    type="date"
                                    label="Date"
                                    variant="standard"
                                    focused
                                    value={values.date}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    error={!!touched.date && !!errors.date}
                                    helperText={touched.date && errors.date}
                                />
                                <TextField
                                    name="targetdate"
                                    type="date"
                                    label="Target Date"
                                    variant="standard"
                                    focused
                                    value={values.targetdate}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    error={!!touched.targetdate && !!errors.targetdate}
                                    helperText={touched.targetdate && errors.targetdate}
                                />
                                <CheckinAutocomplete
                                    name="ProName"
                                    label={
                                        <>
                                            Standard/Activities{" "}
                                            <span style={{ color: "red" }}>*</span>
                                        </>
                                    }
                                    id="ProName"
                                    value={values.ProName}
                                    onChange={(newValue) => {
                                        setFieldValue("ProName", newValue);
                                        setSelectedStandard(newValue);
                                    }}
                                    url={`${listViewurl}?data=${encodeURIComponent(
                                        JSON.stringify({
                                            Query: {
                                                AccessID: "2119",
                                                ScreenName: "Project",
                                                Filter: `parentID=${compID} AND EmployeeID=${EMPID}`,
                                                Any: "",
                                                VerticalLicense: is003Subscription
                                                    ? sliceSubscriptionCode
                                                    : "",
                                            },
                                        })
                                    )}`}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Standard/Activities"
                                            variant="standard"
                                            focused
                                            InputProps={{ ...params.InputProps, readOnly: true }}
                                        />
                                    )}
                                />
                                <CheckinAutocomplete
                                    name="department"
                                    label={
                                        <>
                                            Subject{" "}
                                            <span style={{ color: "red" }}>*</span>
                                        </>
                                    }
                                    id="department"
                                    value={values.department}
                                    focused
                                    onChange={(newValue) => {
                                        setFieldValue("department", newValue);
                                        setSelectedSubject(newValue);
                                    }}
                                    error={!!touched.department && !!errors.department}
                                    helperText={touched.department && errors.department}
                                    url={`${listViewurl}?data=${encodeURIComponent(
                                        JSON.stringify({
                                            Query: {
                                                AccessID: "2172",
                                                ScreenName: "Department",
                                                Filter: `ProjectID=${values.ProName?.RecordID || 0} or AssignedBy=${EMPID}`,
                                                Any: "",
                                                VerticalLicense: is003Subscription
                                                    ? sliceSubscriptionCode
                                                    : "",
                                            },
                                        })
                                    )}`}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Subject"
                                            variant="standard"
                                            focused
                                            InputProps={{ ...params.InputProps, readOnly: true }}
                                        />
                                    )}
                                />
                            </Box>

                            <Stack
                                direction="row"
                                spacing={1.5}
                                justifyContent="flex-end"
                                px={2}
                                pb={2}
                            >
                                <Button
                                    variant="contained"
                                    onClick={() => handleApply(values)}
                                    color="secondary"
                                    sx={{
                                        // background:    "#534ab7",
                                        // background:    "secondary",
                                        // "&:hover":     { background: "#3c3489" },
                                        textTransform: "none",
                                        borderRadius: "8px",
                                        px: 3,
                                        fontSize: "13px",
                                    }}
                                >
                                    Apply
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={() => handleReset(resetForm)}
                                    color="error"
                                    sx={{
                                        // borderColor:   "#e2e8f0",
                                        // color:         "#64748b",
                                        // "&:hover":     { borderColor: "#cbd5e1", background: "#f8fafc" },
                                        // textTransform: "none",
                                        borderRadius: "8px",
                                        px: 3,
                                        fontSize: "13px",
                                    }}
                                >
                                    Reset
                                </Button>
                            </Stack>
                        </form>
                    )}
                </Formik>

                {/* Loading bar */}
                {HWAnalyticsloading && (
                    <LinearProgress sx={{ mx: 2, mb: 1, borderRadius: 1 }} />
                )}

                {/* ── ANALYTICS CONTENT ──────────────────────────────────── */}
                {!HWAnalyticsloading && hasFetched && hasData && (
                    <Box p={2} pt={0} display="flex" flexDirection="column" gap="12px">

                        {/* KPI CARDS */}
                        <Box
                            display="grid"
                            // gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                            gridTemplateColumns={{
                                xs: "1fr",
                                sm: "1fr 1fr",
                                md: "repeat(5, 1fr)",
                            }}
                            gap="10px"
                        >
                            {/* {KPI_CONFIG.map((cfg) => (
                                <KpiCard
                                    key={cfg.key}
                                    cfg={cfg}
                                    value={kpiValues[cfg.key]}
                                    subOverride={
                                        cfg.key === "ev"
                                            ? `${evPct}% complete`
                                            : null
                                    }
                                />
                            ))} */}
                            {KPI_CONFIG.map((cfg) => (
                                <KpiCard
                                    key={cfg.key}
                                    cfg={cfg}
                                    value={kpiValues[cfg.key]}
                                    subOverride={
                                        cfg.key === "total"
                                            ? `${totalHmwrk} Homeworks Assigned`
                                            : cfg.key === "ev"
                                                ? `${evPct}% complete`
                                                : cfg.key === "pe"
                                                    ? `${PePct}% Awaiting Review`
                                                    : cfg.key === "ys"
                                                        ? `${YsPct}% Not Submitted`
                                                        : cfg.key === "NE"
                                                            ? `${NEPct}% Awaiting Evaluation`
                                                            : null
                                    }
                                />
                            ))}

                        </Box>

                        {/* CHARTS ROW */}

                        <Box
                            display="grid"
                            // gridTemplateColumns="minmax(0,1fr) minmax(0,1.6fr)"
                            gridTemplateColumns={{
                                xs: "1fr",          // mobile → stack
                                sm: "1fr",          // tablet → stack
                                md: "1fr 1.6fr",    // desktop → side by side
                            }}
                            gap="12px"
                            sx={{
                                "& > div": {
                                    minWidth: 0,   // 🔥 prevents grid overflow
                                },
                            }}
                        >
                            {/* ── DONUT ── */}
                            <Box sx={cardSx}>
                                <Typography
                                    sx={{ fontSize: "18px", fontWeight: 700, color: "#0f172a", mb: "2px" }}
                                >
                                    Submission Status
                                </Typography>
                                <Typography
                                    sx={{ fontSize: "11px", color: "#94a3b8", mb: "14px" }}
                                >
                                    All Sections
                                </Typography>

                                <Box
                                    display="flex"
                                    alignItems="center"
                                    gap="18px"
                                    flexDirection={{
                                        xs: "column",   // stack
                                        sm: "row",
                                    }}
                                    sx={{
                                        width: "100%",
                                        minWidth: 0,   // 🔥 VERY IMPORTANT
                                    }}
                                >
                                    {/* Donut canvas */}
                                    <Box
                                        sx={{
                                            position: "relative",
                                            width: "100%",
                                            maxWidth: 180,
                                            height: 180,
                                            flexShrink: 0,
                                            margin: "0 auto",
                                            // overflow: "hidden",

                                        }}
                                    >

                                        <Doughnut
                                            data={doughnutData}
                                            key={total}
                                            options={doughnutOptions}
                                        // width={140}
                                        // height={140}
                                        />

                                        {/* Centre label */}
                                        <Box
                                            sx={{
                                                position: "absolute",
                                                top: "50%",
                                                left: "50%",
                                                transform: "translate(-50%,-50%)",
                                                textAlign: "center",
                                                pointerEvents: "none",
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontSize: "24px",
                                                    fontWeight: 500,
                                                    color: "#0f172a",
                                                    lineHeight: 1,
                                                }}
                                            >
                                                {total}
                                            </Typography>
                                            <Typography sx={{ fontSize: "10px", color: "#94a3b8" }}>
                                                Students
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Legend */}
                                    <Box
                                        display="flex"
                                        flexDirection="column"
                                        gap="9px"
                                        // flex={1}
                                        sx={{
                                            flex: 1,
                                            minWidth: 0,        // 🔥 prevents overflow
                                            width: "100%",
                                        }}
                                    >
                                        {PIE_NAMES.map((name, i) => {
                                            const val =
                                                pieData.find((d) => d.name === name)?.value || 0;
                                            return (
                                                <PieLegendRow
                                                    key={name}
                                                    name={name}
                                                    value={val}
                                                    total={total}
                                                    color={PIE_COLORS[i]}
                                                />
                                            );
                                        })}
                                    </Box>
                                </Box>
                            </Box>

                            {/* ── BAR ── */}
                            <Box sx={cardSx}>
                                <Typography
                                    sx={{
                                        fontSize: "18px", fontWeight: 700, color: "#0f172a", mb: "2px"
                                    }}
                                >
                                    Score Distribution
                                </Typography>
                                <Typography
                                    sx={{ fontSize: "11px", color: "#94a3b8", mb: "14px" }}
                                >
                                    Students per mark range
                                </Typography>

                                <Box sx={{
                                    position: "relative",
                                    // height: "170px"
                                    height: {
                                        xs: 200,
                                        sm: 220,
                                        md: 170,
                                    },
                                    overflow: "hidden",

                                }}>
                                    <Bar key={JSON.stringify(barData)} data={barChartData} options={barOptions} />
                                </Box>

                                {/* Bar legend strip */}
                                <Box display="flex" flexWrap="wrap" gap="10px" mt="10px">
                                    {safeBarData.map((d) => (
                                        <Box
                                            key={d.range}
                                            display="flex"
                                            alignItems="center"
                                            gap="5px"
                                        >
                                            <Box
                                                sx={{
                                                    width: 10,
                                                    height: 10,
                                                    borderRadius: "2px",
                                                    background:
                                                        BAR_COLORS[d.range?.trim()] || "#afa9ec",
                                                    flexShrink: 0,
                                                }}
                                            />
                                            <Typography sx={{ fontSize: "11px", color: "#64748b" }}>
                                                {d.range} · {d.value}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </Box>

                        {/* ── EVALUATION TABLE ── */}
                        <Box sx={{ ...cardSx, p: 0, overflow: "hidden" }}>
                            {/* Controls */}
                            <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                px="16px"
                                py="12px"
                                borderBottom="0.5px solid #e2e8f0"
                                flexWrap="wrap"
                                gap={1}
                            >
                                <Typography
                                    sx={{ fontSize: "18px", fontWeight: 700, color: "#0f172a" }}
                                >
                                    Evaluation
                                </Typography>
                                <Box display="flex" gap={1} flexWrap="wrap">
                                    <Select
                                 
    size="small"
    value={statusFilter}
    onChange={(e) => {
        console.log("Selected:", e.target.value);
        setStatusFilter(e.target.value);
        setPage(0);
    }}

                                        sx={{
                                            fontSize: "12px",
                                            minWidth: 130,
                                            "& .MuiOutlinedInput-notchedOutline": {
                                                borderColor: "#e2e8f0",
                                            },
                                            "&:hover .MuiOutlinedInput-notchedOutline": {
                                                borderColor: "#cbd5e1",
                                            },
                                        }}
                                    >
                                        <MenuItem value="All" sx={{ fontSize: "12px" }}>
                                            All Status
                                        </MenuItem>
                                        <MenuItem value="EV" sx={{ fontSize: "12px" }}>
                                            Evaluated
                                        </MenuItem>
                                        <MenuItem value="PE" sx={{ fontSize: "12px" }}>
                                            Pending
                                        </MenuItem>
                                        {/* <MenuItem value="RS" sx={{ fontSize: "12px" }}>
                                            Resubmit
                                        </MenuItem> */}
                                        <MenuItem value="YS" sx={{ fontSize: "12px" }}>
                                            Yet to submit
                                        </MenuItem>
                                    </Select>
                                    <TextField
                                        size="small"
                                        placeholder="Search ..."
                                        value={search}
                                        onChange={(e) => {
                                            setSearch(e.target.value);
                                            setPage(0);
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon
                                                        sx={{ fontSize: 16, color: "#94a3b8" }}
                                                    />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            minWidth: 180,
                                            "& .MuiOutlinedInput-notchedOutline": {
                                                borderColor: "#e2e8f0",
                                            },
                                            "&:hover .MuiOutlinedInput-notchedOutline": {
                                                borderColor: "#cbd5e1",
                                            },
                                            "& input": { fontSize: "12px" },
                                        }}
                                    />
                                </Box>
                            </Box>

                            {/* Table */}
                            <Box sx={{ overflowX: "auto" }}>
                                <Table
                                    size="small"
                                    // sx={{ tableLayout: "fixed", width: "100%" }}
                                    sx={{
                                        tableLayout: "auto",   // 🔥 change from fixed
                                        minWidth: 600,         // ensures scroll instead of break
                                    }}
                                >
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ ...thSx, width: "60px", fontWeight: "bold" }}>#Rank</TableCell>
                                            <TableCell sx={{ ...thSx, width: "90px", fontWeight: "bold" }}>Subject</TableCell>
                                            <TableCell sx={{ ...thSx, width: "90px", fontWeight: "bold" }}>Description</TableCell>
                                            <TableCell sx={{ ...thSx, width: "110px", fontWeight: "bold" }}>Student</TableCell>
                                            <TableCell sx={{ ...thSx, width: "60px", fontWeight: "bold" }}>Score</TableCell>
                                            {/* <TableCell sx={{ ...thSx, width: "130px", fontWeight:"bold" }}>Submission time</TableCell> */}
                                            {/* <TableCell sx={{ ...thSx, width: "140px" }}>Evaluation status</TableCell> */}
                                            <TableCell sx={{ ...thSx, width: "130px", fontWeight: "bold" }}>Status</TableCell>
                                            <TableCell sx={{ ...thSx, fontWeight: "bold" }}>Remarks</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredData
                                            .slice(
                                                page * rowsPerPage,
                                                page * rowsPerPage + rowsPerPage
                                            )
                                            .map((row, i) => (
                                                <TableRow
                                                    key={i}
                                                    sx={{
                                                        "&:hover td": { background: "#f8fafc" },
                                                        "&:last-child td": { border: 0 },
                                                    }}
                                                >
                                                    <TableCell
                                                        sx={{
                                                            ...tdSx,
                                                            fontWeight: 500,
                                                            color: "#534ab7",
                                                        }}
                                                    >
                                                        {row.Rank}
                                                    </TableCell>
                                                    <TableCell sx={tdSx}>{row.Subject}</TableCell>
                                                    <TableCell sx={tdSx}>{row.Description}</TableCell>
                                                    <TableCell
                                                        sx={{ ...tdSx, fontWeight: 500, color: "#0f172a" }}
                                                    >
                                                        {row.Student}
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{ ...tdSx, fontWeight: 500, color: "#0f172a" }}
                                                    >
                                                        {Number(row.Score).toFixed(0)}
                                                    </TableCell>
                                                    {/* <TableCell
                                                        sx={{ ...tdSx, color: "#64748b" }}
                                                    >
                                                        {row.Submission}
                                                    </TableCell> */}
                                                    {/* <TableCell
                                                        sx={{ ...tdSx, color: "#64748b" }}
                                                    >
                                                        {row.Eval}
                                                    </TableCell> */}
                                                    <TableCell sx={tdSx}>
                                                        <StatusBadge code={row.StatusCode} />
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{ ...tdSx, color: "#64748b" }}
                                                    >
                                                        {row.Remarks || "—"}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                    </TableBody>
                                </Table>
                            </Box>

                            <TablePagination
                                component="div"
                                count={filteredData.length}
                                page={page}
                                onPageChange={(e, newPage) => setPage(newPage)}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={(e) => {
                                    setRowsPerPage(parseInt(e.target.value, 10));
                                    setPage(0);
                                }}
                                rowsPerPageOptions={[5, 8, 10, 20]}
                                sx={{
                                    borderTop: "0.5px solid #e2e8f0",
                                    "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                                        { fontSize: "12px" },
                                }}
                            />
                        </Box>
                    </Box>
                )}

                {/* No data */}
                {!HWAnalyticsloading && hasFetched && !hasData && (
                    <Box p={4} display="flex" justifyContent="center" alignItems="center">
                        <Typography sx={{ fontSize: "13px", color: "#94a3b8" }}>
                            No data available for the selected filters.
                        </Typography>
                    </Box>
                )}
            </Paper>
        </React.Fragment>
    );
};

export default HomeAnalytics;
